import _ from "lodash";

export const JAB_ORG_ID = "332a9d47-6e36-11e8-a392-061714f61e3e";
export const PRICING_MODE_WHOLESALE = "wholesale";
export const PRICING_MODE_RETAIL = "retail";
export const RETAIL_MODE_MEMBER_INFO_KEY = "retailMode";
export const RETAIL_TIERS_PREFERENCE_NAME = "pricing-retail-tiers";
export const RETAIL_TIERS_DRIVE_PATH = "private/_system/pricing/retail-tiers.json";

export function isRetailPricingEnabledForOrg(orgId = "") {
	return _.toString(orgId).trim() === JAB_ORG_ID;
}

export function parseMemberInfo(memberInfoRaw = null) {
	if (_.isPlainObject(memberInfoRaw)) {
		return _.cloneDeep(memberInfoRaw);
	}

	if (!_.isString(memberInfoRaw) || _.trim(memberInfoRaw) === "") {
		return {};
	}

	try {
		const parsed = JSON.parse(memberInfoRaw);
		return _.isPlainObject(parsed) ? parsed : {};
	} catch (_err) {
		return {};
	}
}

function isEnabledMemberPreference(value) {
	if (value === true || value === 1) {
		return true;
	}
	if (!_.isString(value)) {
		return false;
	}
	const normalized = _.trim(value).toLowerCase();
	return normalized === "true" || normalized === "1";
}

export function isRetailModeAdministrator(value) {
	return isEnabledMemberPreference(value);
}

export function isMemberRetailModeEnabled(memberInfoRaw = null, orgId = "") {
	if (!isRetailPricingEnabledForOrg(orgId)) {
		return false;
	}

	const memberInfo = parseMemberInfo(memberInfoRaw);
	return isEnabledMemberPreference(memberInfo[RETAIL_MODE_MEMBER_INFO_KEY]);
}

export function getRetailModePolicy(selectedOrg = {}, options = {}) {
	const orgId = _.get(selectedOrg, "id", "");
	const memberInfoRaw = _.get(selectedOrg, ["member", "memberInfo"], null);
	const memberActive = isMemberRetailModeEnabled(memberInfoRaw, orgId);
	const adminPreviewActive = !memberActive
		&& isRetailPricingEnabledForOrg(orgId)
		&& options.adminPreview === true
		&& isRetailModeAdministrator(options.isAdministrator);
	const active = memberActive || adminPreviewActive;

	return {
		active,
		forcedMode: active ? PRICING_MODE_RETAIL : "",
		allowModeSwitch: !active,
		source: memberActive ? "member" : (adminPreviewActive ? "adminPreview" : "")
	};
}

export function isPricingModeForcedForSession(pricingModeForcedAtLaunch = false, currentPolicy = {}) {
	return pricingModeForcedAtLaunch === true || currentPolicy.active === true;
}

export function normalizePricingMode(value = "") {
	const normalized = _.toString(value).trim().toLowerCase();
	return normalized === PRICING_MODE_RETAIL ? PRICING_MODE_RETAIL : PRICING_MODE_WHOLESALE;
}

export function normalizePricingSnapshotAndMode(pricingSnapshotId = "base", pricingMode = "") {
	const normalizedSnapshotId = _.toString(pricingSnapshotId || "base").trim() || "base";
	const normalizedMode = normalizePricingMode(pricingMode);

	if (normalizedSnapshotId.toLowerCase() === PRICING_MODE_RETAIL && !pricingMode) {
		return {
			pricingSnapshotId: "base",
			pricingMode: PRICING_MODE_RETAIL
		};
	}

	return {
		pricingSnapshotId: normalizedSnapshotId,
		pricingMode: normalizedMode
	};
}

export function getActivityPricingContext(activity = {}) {
	const source = activity && typeof activity === "object" ? activity : {};
	let authorProperties = _.isPlainObject(source.authorProperties) ? source.authorProperties : {};
	if (_.isString(source.authorProperties) && _.trim(source.authorProperties)) {
		try {
			const parsedAuthorProperties = JSON.parse(source.authorProperties);
			authorProperties = _.isPlainObject(parsedAuthorProperties) ? parsedAuthorProperties : {};
		} catch (_err) {
			authorProperties = {};
		}
	}

	const pricingSnapshotId = _.toString(source.pricingSnapshotId || authorProperties.pricingSnapshotId || "base").trim() || "base";
	const pricingMode = _.toString(source.pricingMode || authorProperties.pricingMode || "").trim();
	return normalizePricingSnapshotAndMode(pricingSnapshotId, pricingMode);
}

export function resolvePricingContextForSave({
	launchPricingContext = {},
	requestedPricingContext = {},
	latestPricingContext = launchPricingContext,
	pricingModeForcedAtLaunch = false
} = {}) {
	const launch = normalizePricingSnapshotAndMode(
		launchPricingContext.pricingSnapshotId,
		launchPricingContext.pricingMode
	);
	const requested = normalizePricingSnapshotAndMode(
		requestedPricingContext.pricingSnapshotId,
		requestedPricingContext.pricingMode
	);
	const latest = normalizePricingSnapshotAndMode(
		latestPricingContext.pricingSnapshotId,
		latestPricingContext.pricingMode
	);
	const pricingSnapshotChanged = requested.pricingSnapshotId !== launch.pricingSnapshotId;
	const pricingModeChanged = !pricingModeForcedAtLaunch && requested.pricingMode !== launch.pricingMode;

	return {
		pricingSnapshotId: pricingSnapshotChanged ? requested.pricingSnapshotId : latest.pricingSnapshotId,
		pricingMode: pricingModeChanged ? requested.pricingMode : latest.pricingMode,
		pricingSnapshotChanged,
		pricingModeChanged
	};
}

export function getActivityPricingMode(activity = {}) {
	const directPricingMode = _.toString(_.get(activity, "pricingMode", "")).trim();
	if (directPricingMode) {
		return normalizePricingMode(directPricingMode);
	}

	const authorProperties = _.isPlainObject(activity.authorProperties) ? activity.authorProperties : {};
	return normalizePricingMode(authorProperties.pricingMode || "");
}

export function getRetailTiersPreferenceValue(selectedOrg = {}) {
	if (!selectedOrg || !_.isPlainObject(selectedOrg)) {
		return null;
	}

	return _.get(selectedOrg, ["preferencesDefinition", RETAIL_TIERS_PREFERENCE_NAME])
		|| _.get(selectedOrg, ["preferences", RETAIL_TIERS_PREFERENCE_NAME])
		|| null;
}

export function normalizeRetailTiersConfig(config = null) {
	if (!config || typeof config !== "object" || _.isArray(config)) {
		return null;
	}

	if (config.pricingModes || config.retailTiers) {
		return config;
	}

	if (_.isArray(config.ranges) || Object.prototype.hasOwnProperty.call(config, "default")) {
		return {
			retailTiers: config
		};
	}

	return config;
}

export function extractRetailTiersConfig(config = null) {
	const normalizedConfig = normalizeRetailTiersConfig(config);
	if (!normalizedConfig) {
		return null;
	}

	if (_.get(normalizedConfig, ["pricingModes", "retail", "retailTiers"])) {
		return normalizedConfig.pricingModes.retail.retailTiers;
	}

	if (_.get(normalizedConfig, ["retail", "retailTiers"])) {
		return normalizedConfig.retail.retailTiers;
	}

	if (normalizedConfig.retailTiers) {
		return normalizedConfig.retailTiers;
	}

	if (_.isArray(normalizedConfig.ranges) || Object.prototype.hasOwnProperty.call(normalizedConfig, "default")) {
		return normalizedConfig;
	}

	return null;
}

export function hasRetailTiersConfig(config = null) {
	const retailTiers = extractRetailTiersConfig(config);
	if (!retailTiers || typeof retailTiers !== "object" || _.isArray(retailTiers)) {
		return false;
	}

	const defaultMultiplier = retailTiers.default;
	if (defaultMultiplier === null
		|| defaultMultiplier === undefined
		|| typeof defaultMultiplier === "boolean"
		|| (_.isString(defaultMultiplier) && _.trim(defaultMultiplier) === "")) {
		return false;
	}

	const numericDefaultMultiplier = Number(defaultMultiplier);
	return Number.isFinite(numericDefaultMultiplier) && numericDefaultMultiplier >= 0;
}

function stableSerialize(value) {
	if (_.isArray(value)) {
		return `[${value.map(stableSerialize).join(",")}]`;
	}
	if (_.isPlainObject(value)) {
		return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${stableSerialize(value[key])}`).join(",")}}`;
	}
	const serialized = JSON.stringify(value);
	return serialized === undefined ? "null" : serialized;
}

export function getRetailPricingConfigSignature(config = null) {
	if (!hasRetailTiersConfig(config)) {
		return "unavailable";
	}

	const serializedConfig = stableSerialize(normalizeRetailTiersConfig(config));
	let hash = 2166136261;
	for (let index = 0; index < serializedConfig.length; index++) {
		hash ^= serializedConfig.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	return (hash >>> 0).toString(16).padStart(8, "0");
}

function toFiniteNumber(value, fallback = null) {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function roundMoney(value, fallback = 0) {
	const parsed = toFiniteNumber(value, fallback);
	return Number.isFinite(parsed) ? +Number.parseFloat(parsed).toFixed(2) : fallback;
}

function roundUpTo50Cents(value) {
	const numericValue = toFiniteNumber(value, 0);
	return +Number.parseFloat(Math.ceil(numericValue * 2) / 2).toFixed(2);
}

export function computeTierMultiplier(config = null, baseAmount = null) {
	if (config === null || config === undefined) {
		return null;
	}

	const ranges = _.isArray(config) ? config : (_.isArray(config.ranges) ? config.ranges : []);
	const defaultValue = config && typeof config === "object" && config.default !== null && config.default !== undefined
		? toFiniteNumber(config.default, null)
		: null;
	const amount = toFiniteNumber(baseAmount, null);

	if (amount === null) {
		return defaultValue;
	}

	for (let index = 0; index < ranges.length; index++) {
		const range = ranges[index];
		const min = range && range.min !== null && range.min !== undefined
			? toFiniteNumber(range.min, Number.NEGATIVE_INFINITY)
			: Number.NEGATIVE_INFINITY;
		const max = range && range.max !== null && range.max !== undefined
			? toFiniteNumber(range.max, Number.POSITIVE_INFINITY)
			: Number.POSITIVE_INFINITY;

		if (amount >= min && amount <= max) {
			return range && range.value !== null && range.value !== undefined
				? toFiniteNumber(range.value, defaultValue)
				: defaultValue;
		}
	}

	return defaultValue;
}

export function applyPricingModeToRate(baseRate, source = {}, options = {}) {
	const pricingMode = options.pricingMode === undefined
		? PRICING_MODE_WHOLESALE
		: options.pricingMode;
	const pricingModeConfig = options.pricingModeConfig === undefined
		? null
		: options.pricingModeConfig;
	const normalizedMode = normalizePricingMode(pricingMode);
	const numericBaseRate = toFiniteNumber(baseRate, null);

	if (normalizedMode !== PRICING_MODE_RETAIL || numericBaseRate === null) {
		return {
			rate: numericBaseRate,
			pricingMode: normalizedMode,
			pricingModeApplied: false,
			pricingModeDetails: null
		};
	}

	const retailTiers = extractRetailTiersConfig(pricingModeConfig);
	if (!retailTiers) {
		return {
			rate: numericBaseRate,
			pricingMode: normalizedMode,
			pricingModeApplied: false,
			pricingModeDetails: null
		};
	}

	const baseField = _.toString(retailTiers.baseField || "value").trim();
	const sourceBaseValue = source && baseField ? toFiniteNumber(source[baseField], null) : null;
	const tierBase = toFiniteNumber(
		source && source.pricingModeTierBase,
		sourceBaseValue !== null ? sourceBaseValue : numericBaseRate
	);
	const multiplier = computeTierMultiplier(retailTiers, tierBase);

	if (multiplier === null || !Number.isFinite(Number(multiplier))) {
		return {
			rate: numericBaseRate,
			pricingMode: normalizedMode,
			pricingModeApplied: false,
			pricingModeDetails: null
		};
	}

	return {
		rate: numericBaseRate * Number(multiplier),
		pricingMode: normalizedMode,
		pricingModeApplied: true,
		pricingModeDetails: {
			mode: normalizedMode,
			multiplier: Number(multiplier),
			base: numericBaseRate,
			tierBase,
			baseField: baseField || "value"
		}
	};
}

export function shouldApplyPricingModeToInvoiceItem(invoiceItem = {}) {
	const lineType = _.toString(invoiceItem.lineType || invoiceItem.recordType || "").trim().toLowerCase();
	return invoiceItem.skipPricingMode !== true && lineType !== "allowance";
}

export function computeInvoiceItemBaseRateFromParts(invoiceItem = {}) {
	const material = toFiniteNumber(invoiceItem.material, 0);
	const markup = toFiniteNumber(invoiceItem.markup, 0);
	const labor = toFiniteNumber(invoiceItem.labor, 0);
	const labortime = toFiniteNumber(invoiceItem.labortime, 0);
	const finish = toFiniteNumber(invoiceItem.finish, 0);
	const credit = toFiniteNumber(invoiceItem.credit, 0);
	const computed = (material * markup) + (labor * labortime) + finish - credit;
	return Number.isFinite(computed) && Math.abs(computed) > 0.004 ? computed : null;
}

export function deriveInvoiceItemUnitRateFromValue(invoiceItem = {}) {
	const explicitValue = toFiniteNumber(invoiceItem.value, null);
	if (explicitValue === null || Math.abs(explicitValue) <= 0.004) {
		return null;
	}

	const qty = toFiniteNumber(invoiceItem.qty, 1);
	const discount = toFiniteNumber(invoiceItem.discount, 1);
	const denominator = (qty || 1) * (discount || 1);
	if (!Number.isFinite(denominator) || Math.abs(denominator) <= 0.004) {
		return roundMoney(explicitValue, explicitValue);
	}

	return roundMoney(explicitValue / denominator, explicitValue);
}

export function resolveInvoiceItemBaseRate(invoiceItem = {}, fallback = null) {
	const computedPartRate = computeInvoiceItemBaseRateFromParts(invoiceItem);
	const derivedValueRate = deriveInvoiceItemUnitRateFromValue(invoiceItem);
	const fallbackUnitRate = [
		derivedValueRate,
		fallback,
		invoiceItem.rate
	].reduce((resolved, candidate) => (
		resolved !== null ? resolved : toFiniteNumber(candidate, null)
	), null);
	const candidates = [
		computedPartRate,
		invoiceItem.pricingModeBaseRate,
		invoiceItem.wholesaleRate,
		invoiceItem.baseRate,
		_.get(invoiceItem, ["pricingModeDetails", "base"]),
		invoiceItem.pricingBaseValue,
		invoiceItem.baseValue,
		derivedValueRate,
		fallback,
		invoiceItem.rate
	];

	for (let index = 0; index < candidates.length; index++) {
		const value = toFiniteNumber(candidates[index], null);
		if (value !== null) {
			if (
				Math.abs(value) <= 0.004
				&& fallbackUnitRate !== null
				&& Math.abs(fallbackUnitRate) > 0.004
			) {
				continue;
			}
			return value;
		}
	}

	return fallbackUnitRate !== null ? fallbackUnitRate : 0;
}

export function resolveInvoiceItemTierBase(invoiceItem = {}, baseRate = null) {
	const computedPartRate = computeInvoiceItemBaseRateFromParts(invoiceItem);
	const normalizedBaseRate = toFiniteNumber(baseRate, null);
	const candidates = [
		computedPartRate,
		invoiceItem.pricingBaseValue,
		invoiceItem.baseValue,
		_.get(invoiceItem, ["pricingModeDetails", "tierBase"]),
		invoiceItem.pricingModeTierBase,
		baseRate
	];

	for (let index = 0; index < candidates.length; index++) {
		const value = toFiniteNumber(candidates[index], null);
		if (value !== null) {
			if (
				Math.abs(value) <= 0.004
				&& normalizedBaseRate !== null
				&& Math.abs(normalizedBaseRate) > 0.004
			) {
				continue;
			}
			return value;
		}
	}

	return baseRate || 0;
}

export function syncInvoiceItemValue(invoiceItem = {}) {
	const normalizedItem = { ...invoiceItem };
	const qty = toFiniteNumber(normalizedItem.qty, 0);
	const explicitValue = toFiniteNumber(normalizedItem.value, null);
	let rate = toFiniteNumber(normalizedItem.rate, null);

	if ((rate === null || Math.abs(rate) <= 0.004) && explicitValue !== null && Math.abs(explicitValue) > 0.004) {
		const derivedRate = deriveInvoiceItemUnitRateFromValue(normalizedItem);
		if (derivedRate !== null) {
			normalizedItem.rate = derivedRate;
			rate = derivedRate;
		}
	}

	const discount = toFiniteNumber(normalizedItem.discount, 1);
	if (rate === null) {
		if (explicitValue !== null) {
			normalizedItem.value = roundMoney(explicitValue, 0);
			return normalizedItem;
		}
		rate = 0;
	}

	const value = rate * qty * discount;
	normalizedItem.value = Number.isFinite(value) ? roundMoney(value, 0) : 0;
	return normalizedItem;
}

function normalizeInvoiceItemPricingFields(invoiceItem = {}) {
	const normalizedItem = { ...invoiceItem };
	if (!shouldApplyPricingModeToInvoiceItem(normalizedItem)) {
		return syncInvoiceItemValue(normalizedItem);
	}

	const baseRate = resolveInvoiceItemBaseRate(normalizedItem, normalizedItem.rate);
	const tierBase = resolveInvoiceItemTierBase(normalizedItem, baseRate);
	normalizedItem.pricingModeBaseRate = roundMoney(baseRate, 0);
	normalizedItem.wholesaleRate = normalizedItem.pricingModeBaseRate;
	normalizedItem.pricingBaseValue = normalizedItem.pricingModeBaseRate;
	normalizedItem.pricingModeTierBase = roundMoney(tierBase, normalizedItem.pricingModeBaseRate);
	return syncInvoiceItemValue(normalizedItem);
}

export function applyPricingModeToInvoiceItem(
	invoiceItem = {},
	pricingMode = PRICING_MODE_WHOLESALE,
	pricingModeConfig = null
) {
	if (!invoiceItem || typeof invoiceItem !== "object") {
		return invoiceItem;
	}

	const normalizedMode = normalizePricingMode(pricingMode);
	let normalizedItem = normalizeInvoiceItemPricingFields(invoiceItem);

	if (!shouldApplyPricingModeToInvoiceItem(normalizedItem)) {
		normalizedItem.pricingMode = normalizedMode;
		return syncInvoiceItemValue(normalizedItem);
	}

	const baseRate = resolveInvoiceItemBaseRate(normalizedItem, normalizedItem.rate);
	const tierBase = resolveInvoiceItemTierBase(normalizedItem, baseRate);
	const adjustedRate = applyPricingModeToRate(baseRate, {
		...normalizedItem,
		value: normalizedItem.pricingBaseValue === undefined || normalizedItem.pricingBaseValue === null
			? baseRate
			: normalizedItem.pricingBaseValue,
		pricingModeTierBase: tierBase
	}, {
		pricingMode: normalizedMode,
		pricingModeConfig
	});
	const activeRate = toFiniteNumber(adjustedRate.rate, baseRate);

	normalizedItem.rate = normalizedItem.roundUp
		? roundUpTo50Cents(activeRate)
		: roundMoney(activeRate, 0);
	normalizedItem.rate = roundMoney(normalizedItem.rate, 0);
	normalizedItem.pricingMode = normalizedMode;
	normalizedItem.pricingModeBaseRate = roundMoney(baseRate, 0);
	normalizedItem.wholesaleRate = normalizedItem.pricingModeBaseRate;
	normalizedItem.pricingBaseValue = normalizedItem.pricingModeBaseRate;
	normalizedItem.pricingModeTierBase = roundMoney(tierBase, normalizedItem.pricingModeBaseRate);

	if (adjustedRate.pricingModeDetails) {
		normalizedItem.pricingModeDetails = {
			...adjustedRate.pricingModeDetails,
			base: normalizedItem.pricingModeBaseRate,
			tierBase: normalizedItem.pricingModeTierBase
		};
	} else {
		delete normalizedItem.pricingModeDetails;
	}

	normalizedItem = syncInvoiceItemValue(normalizedItem);
	return normalizedItem;
}

export function applyPricingModeToInvoiceItems(
	invoiceItems = [],
	pricingMode = PRICING_MODE_WHOLESALE,
	pricingModeConfig = null
) {
	return (_.isArray(invoiceItems) ? invoiceItems : []).map(invoiceItem => (
		applyPricingModeToInvoiceItem(invoiceItem, pricingMode, pricingModeConfig)
	));
}
